import { useState } from "react";
import { submitRecommendationFeedback } from "../../api/search";

/**
 * Thumbs up / down feedback for a WHOLE batch of results (not a single game).
 * Shown to logged-in users under a set of search results / recommendations.
 * On submit it stores the feedback and lets the backend gently adjust the
 * user's recommendation weighting so future results improve.
 *
 * Remount this (via a `key` tied to the search) to reset it for a new search.
 */
const RecommendationFeedback = ({ filters = {}, gameIds = [], context = "homepage" }) => {
  const [choice, setChoice] = useState(null); // 'up' | 'down' | null
  const [submitting, setSubmitting] = useState(false);

  const handleVote = async (feedback) => {
    if (choice || submitting) return;
    setSubmitting(true);
    setChoice(feedback); // optimistic
    const res = await submitRecommendationFeedback(feedback, {
      filters,
      gameIds,
      context,
    });
    if (!res || res.success === false) {
      setChoice(null); // allow retry on failure
    }
    setSubmitting(false);
  };

  if (choice) {
    return (
      <div className="flex items-center gap-2 text-sm text-white/70">
        <span className="text-brand-gold">✓</span>
        {choice === "up"
          ? "Thanks! We'll show you more like these."
          : "Thanks — we'll fine-tune your recommendations."}
      </div>
    );
  }

  const baseBtn =
    "flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-sm font-semibold transition-all duration-200 disabled:opacity-50";

  return (
    <div className="flex items-center gap-3">
      <span className="text-white/60 text-sm">Were these a good match?</span>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => handleVote("up")}
          disabled={submitting}
          title="Yes — show me more like these"
          aria-label="Thumbs up"
          className={`${baseBtn} border-lime-400/60 text-lime-400 hover:bg-lime-400/10 hover:-translate-y-0.5`}
        >
          <span>👍</span>
        </button>
        <button
          type="button"
          onClick={() => handleVote("down")}
          disabled={submitting}
          title="Not really — improve my recommendations"
          aria-label="Thumbs down"
          className={`${baseBtn} border-brand-gold/60 text-brand-gold hover:bg-brand-gold/10 hover:-translate-y-0.5`}
        >
          <span>👎</span>
        </button>
      </div>
    </div>
  );
};

export default RecommendationFeedback;
